import React from 'react';
import {
  Archive,
  Check,
  ChevronDown,
  Columns3,
  Download,
  MoreHorizontal,
  Pencil,
  QrCode,
  StickyNote,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';

import type { ContainerType } from '../../ContainerComponents/ContainerPage';
import ContainerTypeThumbnail from './ContainerTypeThumbnail';
import {
  DEFAULT_COLUMN_VISIBILITY,
  EDITED_PILL_STYLE,
  getClassPillStyle,
  getSegmentDotColor,
  OPTIONAL_COLUMNS,
  PRICING_REF_COLUMNS,
  WARNING_ACCENT,
  type PriceFieldDef,
  type SystemType,
} from './constants';

export type TableMode = 'details' | 'pricing';

type ContainerTypesTableProps = {
  /** The current server page, with pending edits applied. */
  rows: ContainerType[];
  /** Rows matching the active filters (across all pages). */
  totalCount: number;
  /** Rows in the archived partition regardless of other filters. */
  unfilteredCount: number;
  pageIndex: number;
  pageCount: number;
  onPageChange: (pageIndex: number) => void;
  mode: TableMode;
  systemType: SystemType;
  isAdministrator: boolean;
  groupNameById: Map<number, string>;
  dirtyIds: number[];
  displayName: (ct: ContainerType) => string;
  /** Formatted EUR reference value for the pricing-mode columns. */
  getEurReference: (ct: ContainerType, def: PriceFieldDef) => string;
  onOpen: (ct: ContainerType) => void;
  onDownloadQr: (ct: ContainerType) => void;
  onPrintLabel: (ct: ContainerType) => void;
  onBulkArchive: (rows: ContainerType[]) => void;
  onExport: (rows: ContainerType[]) => void;
};

/** Windowed page list with ellipses (mirrors the Orders admin pagination). */
function getPageNumbers(
  current: number,
  total: number,
): (number | 'ellipsis')[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | 'ellipsis')[] = [1];
  if (current > 3) pages.push('ellipsis');

  for (
    let i = Math.max(2, current - 1);
    i <= Math.min(total - 1, current + 1);
    i++
  ) {
    pages.push(i);
  }

  if (current < total - 2) pages.push('ellipsis');
  pages.push(total);

  return pages;
}

const alignClass = (align?: string) =>
  align === 'right'
    ? 'tw:text-right'
    : align === 'center'
      ? 'tw:text-center'
      : 'tw:text-left';

/**
 * Desktop results area: selection/bulk toolbar, the data table (details mode
 * columns or the EUR pricing reference columns) and pagination. Filtering and
 * paging happen server-side; this component renders one page. Selection
 * caches the full row objects so bulk actions keep working across page flips.
 */
export default function ContainerTypesTable({
  rows,
  totalCount,
  unfilteredCount,
  pageIndex,
  pageCount,
  onPageChange,
  mode,
  systemType,
  isAdministrator,
  groupNameById,
  dirtyIds,
  displayName,
  getEurReference,
  onOpen,
  onDownloadQr,
  onPrintLabel,
  onBulkArchive,
  onExport,
}: ContainerTypesTableProps) {
  const [selected, setSelected] = React.useState<Record<number, ContainerType>>(
    {},
  );
  const [columnVisibility, setColumnVisibility] = React.useState<
    Record<string, boolean>
  >(() => {
    const defaults: Record<string, boolean> = {};
    OPTIONAL_COLUMNS.forEach((col) => {
      defaults[col.id] = DEFAULT_COLUMN_VISIBILITY[col.id] ?? false;
    });
    return defaults;
  });

  const pageRows = rows;
  const selectedRows = Object.values(selected);
  const clearSelection = () => setSelected({});

  const allPageSelected =
    pageRows.length > 0 && pageRows.every((ct) => selected[ct.id] != null);
  const somePageSelected = pageRows.some((ct) => selected[ct.id] != null);

  const togglePage = (value: boolean) => {
    setSelected((prev) => {
      const next = { ...prev };
      pageRows.forEach((ct) => {
        if (value) next[ct.id] = ct;
        else delete next[ct.id];
      });
      return next;
    });
  };

  const visibleOptionalColumns =
    mode === 'details'
      ? OPTIONAL_COLUMNS.filter((col) => columnVisibility[col.id])
      : [];
  const refColumns = mode === 'pricing' ? PRICING_REF_COLUMNS[systemType] : [];
  const columnCount =
    3 + visibleOptionalColumns.length + (mode === 'pricing' ? 3 : 0);

  const renderCell = (columnId: string, ct: ContainerType) => {
    switch (columnId) {
      case 'id':
        return (
          <span className="tw:font-mono tw:text-[13px] tw:text-muted-foreground">
            {ct.id}
          </span>
        );
      case 'class': {
        const style = getClassPillStyle(ct.class);
        return ct.class ? (
          <span
            className="tw:inline-flex tw:h-[22px] tw:items-center tw:rounded-full tw:px-2.5 tw:text-xs tw:font-medium"
            style={{
              backgroundColor: style.backgroundColor,
              color: style.color,
              border: style.border,
            }}
          >
            {ct.class}
          </span>
        ) : null;
      }
      case 'group':
        return (
          <span className="tw:text-foreground">
            {groupNameById.get(ct.groupId) ?? ''}
          </span>
        );
      case 'segment':
        return ct.segment ? (
          <span className="tw:inline-flex tw:h-[22px] tw:items-center tw:gap-1.5 tw:rounded-full tw:bg-muted tw:px-2.5 tw:text-xs tw:font-medium tw:text-foreground">
            <span
              className="tw:inline-block tw:size-2 tw:rounded-full"
              style={{ backgroundColor: getSegmentDotColor(ct.segment) }}
            />
            {i18n.t(`EditContainerInfo:segment${ct.segment}`)}
          </span>
        ) : (
          <span className="tw:text-muted-foreground">—</span>
        );
      case 'sizeHelper':
        return (
          <span className="tw:font-medium tw:text-foreground">
            {ct.sizeHelper}
          </span>
        );
      case 'sku':
        return (
          <span className="tw:font-mono tw:text-[13px] tw:text-foreground">
            {ct.sku}
          </span>
        );
      case 'countries': {
        const codes = ct.countryCodes ?? [];
        const preview = codes.slice(0, 3);
        const more = codes.length - preview.length;
        return (
          <div className="tw:flex tw:items-center tw:gap-1">
            {preview.map((code) => (
              <span
                key={code}
                className="tw:rounded tw:bg-muted tw:px-1.5 tw:py-0.5 tw:font-mono tw:text-[11px] tw:text-muted-foreground"
              >
                {code}
              </span>
            ))}
            {more > 0 && (
              <span className="tw:text-[11px] tw:text-muted-foreground">
                +{more}
              </span>
            )}
          </div>
        );
      }
      case 'weight':
        return (
          <span className="tw:tabular-nums tw:text-foreground">
            {ct.weight != null && String(ct.weight) !== ''
              ? i18n.t('EditContainerInfo:grams', { n: ct.weight })
              : ''}
          </span>
        );
      case 'packingUnit':
        return (
          <span className="tw:tabular-nums tw:text-muted-foreground">
            {ct.packingUnit}
          </span>
        );
      case 'branding':
        return <span className="tw:text-foreground">{ct.branding}</span>;
      case 'legalOwner':
        return <span className="tw:text-foreground">{ct.legalOwner}</span>;
      case 'color':
        return ct.hexCode ? (
          <span className="tw:inline-flex tw:items-center tw:gap-2">
            <span
              className="tw:size-[15px] tw:rounded tw:border tw:border-border"
              style={{ backgroundColor: `#${ct.hexCode}` }}
            />
            <span className="tw:font-mono tw:text-xs tw:text-muted-foreground">
              #{ct.hexCode.toUpperCase()}
            </span>
          </span>
        ) : null;
      case 'orderKey':
        return (
          <span className="tw:tabular-nums tw:text-muted-foreground">
            {ct.orderKey}
          </span>
        );
      case 'archived':
        return ct.archived ? (
          <Check size={15} className="tw:mx-auto tw:text-foreground" />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      {/* Results toolbar */}
      <div className="tw:flex tw:min-h-[34px] tw:items-center tw:justify-between tw:gap-3">
        <div className="tw:flex tw:items-center tw:gap-3">
          {selectedRows.length > 0 ? (
            <>
              <span className="tw:text-sm tw:font-semibold">
                {i18n.t('Common:countSelected', {
                  count: selectedRows.length,
                })}
              </span>
              <span className="tw:h-[18px] tw:w-px tw:bg-border" />
              {isAdministrator && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onBulkArchive(selectedRows);
                    clearSelection();
                  }}
                >
                  <Archive size={15} />
                  {i18n.t('EditContainerInfo:archive')}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onExport(selectedRows)}
              >
                <Download size={15} />
                {i18n.t('EditContainerInfo:export')}
              </Button>
              <Button variant="ghost" size="sm" onClick={clearSelection}>
                {i18n.t('EditContainerInfo:clear')}
              </Button>
            </>
          ) : (
            <span className="tw:text-sm tw:text-muted-foreground">
              {i18n.t('EditContainerInfo:showingCountPre')}{' '}
              <b className="tw:font-semibold tw:text-foreground">
                {totalCount}
              </b>{' '}
              {i18n.t('EditContainerInfo:showingCountPost', {
                total: unfilteredCount,
              })}
            </span>
          )}
        </div>
        {mode === 'pricing' ? (
          <span className="tw:text-[13px] tw:text-muted-foreground">
            {i18n.t('EditContainerInfo:pricingReferenceNote')}
          </span>
        ) : (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Columns3 size={15} />
                {i18n.t('EditContainerInfo:columns')}
                <ChevronDown size={14} />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="tw:w-[210px] tw:gap-0 tw:p-1.5"
            >
              <div className="tw:px-2 tw:pt-1.5 tw:pb-2 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                {i18n.t('EditContainerInfo:toggleColumns')}
              </div>
              <div className="tw:flex tw:max-h-[320px] tw:flex-col tw:overflow-auto">
                {OPTIONAL_COLUMNS.map((col) => {
                  const checked = columnVisibility[col.id];
                  return (
                    <button
                      key={col.id}
                      type="button"
                      onClick={() =>
                        setColumnVisibility((prev) => ({
                          ...prev,
                          [col.id]: !prev[col.id],
                        }))
                      }
                      className="tw:flex tw:w-full tw:items-center tw:gap-2.5 tw:rounded-sm tw:px-2 tw:py-1.5 tw:text-left tw:text-sm tw:outline-none tw:cursor-pointer tw:hover:bg-accent"
                    >
                      <span
                        className={cn(
                          'tw:flex tw:size-[17px] tw:flex-none tw:items-center tw:justify-center tw:rounded-[4px] tw:border',
                          checked
                            ? 'tw:border-primary tw:bg-primary tw:text-primary-foreground'
                            : 'tw:border-input',
                        )}
                      >
                        {checked && <Check size={12} />}
                      </span>
                      {i18n.t(`EditContainerInfo:${col.labelKey}`)}
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Table */}
      <div className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-border">
        <div className="tw:overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="tw:w-11 tw:pl-4">
                  <Checkbox
                    checked={
                      allPageSelected
                        ? true
                        : somePageSelected
                          ? 'indeterminate'
                          : false
                    }
                    onCheckedChange={(value) => togglePage(!!value)}
                    aria-label={i18n.t('Common:selectAllItemsAcrossPages')}
                  />
                </TableHead>
                <TableHead className="tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                  {i18n.t('EditContainerInfo:colContainer')}
                </TableHead>
                {visibleOptionalColumns.map((col) => (
                  <TableHead
                    key={col.id}
                    className={cn(
                      'tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-muted-foreground',
                      alignClass(col.align),
                    )}
                  >
                    {i18n.t(`EditContainerInfo:${col.labelKey}`)}
                  </TableHead>
                ))}
                {refColumns.map((col) => (
                  <TableHead
                    key={col.labelKey}
                    className="tw:whitespace-nowrap tw:text-right tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-muted-foreground"
                  >
                    {i18n.t(`EditContainerInfo:${col.labelKey}`)}
                  </TableHead>
                ))}
                <TableHead className="tw:w-[120px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.length === 0 && (
                <TableRow className="tw:hover:bg-transparent">
                  <TableCell
                    colSpan={columnCount}
                    className="tw:h-32 tw:text-center tw:text-sm tw:text-muted-foreground"
                  >
                    {i18n.t('EditContainerInfo:emptyState')}
                  </TableCell>
                </TableRow>
              )}
              {pageRows.map((ct) => {
                const rowDirty = dirtyIds.includes(ct.id);
                const isSelected = selected[ct.id] != null;
                const accent = rowDirty
                  ? WARNING_ACCENT
                  : isSelected
                    ? 'var(--primary)'
                    : null;
                return (
                  <TableRow
                    key={ct.id}
                    onClick={() => onOpen(ct)}
                    className={cn(
                      'tw:cursor-pointer tw:hover:bg-accent',
                      isSelected && 'tw:bg-muted',
                    )}
                    style={
                      accent
                        ? { boxShadow: `inset 3px 0 0 0 ${accent}` }
                        : undefined
                    }
                  >
                    <TableCell
                      className="tw:w-11 tw:pl-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(value) =>
                          setSelected((prev) => {
                            const next = { ...prev };
                            if (value) next[ct.id] = ct;
                            else delete next[ct.id];
                            return next;
                          })
                        }
                        aria-label={i18n.t('Common:select')}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="tw:flex tw:items-center tw:gap-3">
                        <ContainerTypeThumbnail
                          imageUrl={ct.imageUrl}
                          hexCode={ct.hexCode}
                          segment={ct.segment}
                          size={40}
                        />
                        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
                          <div className="tw:flex tw:items-center tw:gap-2">
                            <span className="tw:whitespace-nowrap tw:text-sm tw:font-medium">
                              {displayName(ct)}
                            </span>
                            {rowDirty && (
                              <span
                                className="tw:inline-flex tw:h-[18px] tw:items-center tw:rounded-full tw:px-1.5 tw:text-[10px] tw:font-semibold"
                                style={{
                                  backgroundColor:
                                    EDITED_PILL_STYLE.backgroundColor,
                                  color: EDITED_PILL_STYLE.color,
                                  border: EDITED_PILL_STYLE.border,
                                }}
                              >
                                {i18n.t('EditContainerInfo:edited')}
                              </span>
                            )}
                          </div>
                          <span className="tw:font-mono tw:text-[11px] tw:text-muted-foreground">
                            {i18n.t('EditContainerInfo:rowSubline', {
                              id: ct.id,
                              order: ct.orderKey,
                            })}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    {visibleOptionalColumns.map((col) => (
                      <TableCell key={col.id} className={alignClass(col.align)}>
                        {renderCell(col.id, ct)}
                      </TableCell>
                    ))}
                    {refColumns.map((col) => (
                      <TableCell
                        key={col.labelKey}
                        className="tw:whitespace-nowrap tw:text-right tw:font-mono tw:text-[13px] tw:tabular-nums"
                      >
                        {getEurReference(ct, col.def)}
                      </TableCell>
                    ))}
                    <TableCell
                      className="tw:w-[120px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="tw:flex tw:items-center tw:justify-end tw:gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={i18n.t('Common:edit')}
                          title={i18n.t('Common:edit')}
                          onClick={() => onOpen(ct)}
                        >
                          <Pencil size={15} />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={i18n.t(
                                'EditContainerInfo:moreActions',
                              )}
                              title={i18n.t('EditContainerInfo:moreActions')}
                            >
                              <MoreHorizontal size={15} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="tw:min-w-[210px]"
                          >
                            <DropdownMenuItem onClick={() => onDownloadQr(ct)}>
                              <QrCode size={15} />
                              {i18n.t('EditContainerInfo:downloadQr')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onPrintLabel(ct)}>
                              <StickyNote size={15} />
                              {i18n.t('EditContainerInfo:printLabel')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
        <span className="tw:text-sm tw:text-muted-foreground">
          {i18n.t('EditContainerInfo:paginationInfo', {
            page: totalCount === 0 ? 0 : pageIndex + 1,
            pageCount: totalCount === 0 ? 0 : pageCount,
            count: totalCount,
          })}
        </span>
        {pageCount > 1 && (
          <Pagination className="tw:mx-0 tw:w-auto tw:justify-end">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => pageIndex > 0 && onPageChange(pageIndex - 1)}
                  aria-disabled={pageIndex === 0}
                  className={cn(
                    'tw:cursor-pointer',
                    pageIndex === 0 && 'tw:pointer-events-none tw:opacity-50',
                  )}
                />
              </PaginationItem>
              {getPageNumbers(pageIndex + 1, pageCount).map((page, i) =>
                page === 'ellipsis' ? (
                  <PaginationItem key={`ellipsis-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={page}>
                    <PaginationLink
                      isActive={page === pageIndex + 1}
                      onClick={() => onPageChange(page - 1)}
                      className="tw:cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    pageIndex + 1 < pageCount && onPageChange(pageIndex + 1)
                  }
                  aria-disabled={pageIndex + 1 >= pageCount}
                  className={cn(
                    'tw:cursor-pointer',
                    pageIndex + 1 >= pageCount &&
                      'tw:pointer-events-none tw:opacity-50',
                  )}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  );
}
